import DbService from 'resources/db.service';
import { getJsNow } from 'utils/date';
import { TASK_STATUSES, TASK_TYPES } from 'resources/task/task.constants';
import schema from './task.schema';

const service = new DbService('tasks', { schema });

const createTask = async ({
  id,
  milestoneId,
  milestoneOrder,
  companyId,
  dealId,
  templateId,
  assigneeId,
  content,
  assigneeType,
  description,
  predefined,
  isOptional,
  isHiddenFromClient,
  isAuto,
  type,
  // due date fields
  dueInDays,
  isAfterAction,
  dueRelativeDate,
  sendTime,
  timeZone,
}) => {
  const requiredFields = {
    milestone_id: milestoneId,
    milestone_order: milestoneOrder,
    company_id: companyId,
    content,
    status: TASK_STATUSES.OPEN,
    due_in_days: dueInDays,
    is_hidden_from_client: isHiddenFromClient,
    is_after_action: true,
  };

  if (typeof isAfterAction === 'boolean') {
    requiredFields.is_after_action = isAfterAction;
  }

  const optionalFields = {
    ...(id && { id }),
    ...(dealId && { deal_id: dealId }),
    ...(templateId && { template_id: templateId }),
    ...(assigneeId && { assignee_id: assigneeId }),
    ...(assigneeType && { assignee_type: assigneeType }),
    ...(type && { type }),
    ...(description && { description }),
    ...(predefined && { predefined }),
    ...(isOptional && { is_optional: isOptional }),
    ...(isAuto && { is_auto: isAuto }),
    ...(dueRelativeDate && { due_relative_date: dueRelativeDate }),
    ...(sendTime && { send_time: sendTime }),
    ...(timeZone && { time_zone: timeZone }),
  };

  const taskResult = await service.create({
    ...requiredFields,
    ...optionalFields,
  });

  return taskResult.data[0];
};

const updateTask = async (taskData) => {
  const response = await service
    .updateOne({ id: taskData.id }, taskData);

  if (response.isBadRequest) {
    return null;
  }

  return response.data[0];
};

const removeTask = async (id) => {
  const response = await service.remove({ id });

  if (response.isBadRequest) {
    return null;
  }

  return response.data[0];
};

const getMilestoneTasks = async (milestoneId) => {
  // const response = await service
  //   .select()
  //   .match({ milestone_id: milestoneId })
  //   .order('milestone_order', { ascending: true });

  let response = await service.selectByKey({
    selectKey: 'getMilestoneTasks',
    params: { milestoneId },
  });

  const { data } = service.processResponse(response);

  return data;
};

const getMilestonesTasks = async (milestoneIds) => {
  // const response = await service
  //   .select()
  //   .in('milestone_id', milestoneIds)
  //   .order('milestone_order', { ascending: true });

  let response = await service.publicSelectByKey({
    selectKey: 'getMilestonesTasks',
    params: { milestoneIds },
  });

  const { data } = service.processResponse(response);

  return data;
};

const getMilestoneOpenEmails = async (milestoneId) => {
  // const response = await service
  //   .select()
  //   .match({
  //     milestone_id: milestoneId,
  //     status: TASK_STATUSES.OPEN,
  //     type: TASK_TYPES.EMAIL_TEMPLATE,
  //   })
  //   .not('deal_id', 'is', null)
  //   .not('template_id', 'is', null)
  //   .select(`
  //     *,
  //     deal:deal_id (*),
  //     milestone:milestone_id (*),
  //     template:template_id (*)
  //   `);

  let response = await service.selectByKey({
    selectKey: 'getMilestoneOpenEmails',
    params: { milestoneId },
  });

  const { data } = service.processResponse(response);

  return data;
};

const getTasksByTemplateId = async (templateId) => {
  const data = await service.find({
    template_id: templateId,
    type: TASK_TYPES.EMAIL_TEMPLATE,
  });

  return data;
};

const getDocumentRequestsByDealId = async (dealId) => {
  const data = await service.find({
    deal_id: dealId,
    type: TASK_TYPES.DOCUMENT_REQUEST,
  });

  return data;
};

const getClientDocumentRequestsByDealId = async (dealId) => {
  const data = await service.find({
    deal_id: dealId,
    type: TASK_TYPES.DOCUMENT_REQUEST,
    is_hidden_from_client: false,
  });

  return data;
};

const findRequiredOpenUnderContractTasks = async (underContractMilestoneIds) => {
  // let response = await service
  //   .select()
  //   .match({
  //     status: TASK_STATUSES.OPEN,
  //     is_optional: false,
  //     is_skipped: false,
  //   })
  //   .in('milestone_id', underContractMilestoneIds);
  
  let response = await service.selectByKey({
    selectKey: 'findRequiredOpenUnderContractTasks',
    params: { underContractMilestoneIds },
  });

  response = service.processResponse(response);

  return response?.data;
};

const findClientTasksByDealId = async (dealId) => {
  // let response = await service
  //   .select()
  //   .not('deal_id', 'is', null)
  //   .not('is_hidden_from_client', 'is', true)
  //   .match({ deal_id: dealId });

  let response = await service.selectByKey({
    selectKey: 'findClientTasksByDealId',
    params: { dealId },
  });

  response = service.processResponse(response);

  return response?.data;
};

const getTasksByDealId = async (dealId) => {
  // let response = await service
  //   .select()
  //   .not('deal_id', 'is', null)
  //   .match({ deal_id: dealId });

  let response = await service.publicSelectByKey({
    selectKey: 'getTasksByDealId',
    params: { dealId },
  });

  response = service.processResponse(response);

  return response?.data;
};

const getOpenTasksByDealIds = async (dealIds) => {
  // let response = await service
  //   .select()
  //   .in('deal_id', dealIds)
  //   .match({
  //     status: TASK_STATUSES.OPEN,
  //   })
  //   .select(`
  //     *,
  //     deal:deal_id (*),
  //     milestone:milestone_id (*)
  //   `);

  let response = await service.selectByKey({
    selectKey: 'getOpenTasksByDealIds',
    params: { dealIds },
  });

  response = service.processResponse(response);

  return response?.data;
};

const completePredefineTask = async ({
  dealId,
  taskContent,
  completedBy,
}) => {
  const response = await service.updateOne(
    { deal_id: dealId, content: taskContent },
    {
      status: TASK_STATUSES.COMPLETED,
      completed_on: getJsNow(),
      completed_by: completedBy,
    },
  );

  return response.data[0];
};

const getNumOfUnsentEmails = async ({ fromEmail }) => {
  let response = await service.selectByKey({
    selectKey: 'getNumOfUnsentEmails',
    params: { fromEmail },
  });

  response = service.processResponse(response);

  return response;
};

// --> tasks page methods
const findTasksCompletedByClients = async (clientIds) => {
  // let response = await service
  //   .select()
  //   .match({ status: TASK_STATUSES.COMPLETED })
  //   .in('completed_by', clientIds)
  //   .select(`
  //     *,
  //     deal:deal_id (*),
  //     milestone:milestone_id (*)
  //   `);

  let response = await service.selectByKey({
    selectKey: 'findTasksCompletedByClients',
    params: { clientIds },
  });

  response = service.processResponse(response);

  return response?.data;
};

const findTasksCompletedByRealtorInDeals = async ({
  realtorId,
  dealIds,
}) => {
  // let response = await service
  //   .select()
  //   .match({ status: TASK_STATUSES.COMPLETED, completed_by: realtorId })
  //   .in('deal_id', dealIds)
  //   .select(`
  //     *,
  //     deal:deal_id (*),
  //     milestone:milestone_id (*)
  //   `);

  let response = await service.selectByKey({
    selectKey: 'findTasksCompletedByRealtorInDeals',
    params: { realtorId, dealIds },
  });

  response = service.processResponse(response);

  return response?.data;
};

const findOpenTasks = async (assigneeId) => {
  // let response = await service
  //   .select()
  //   .match({ assignee_id: assigneeId, status: TASK_STATUSES.OPEN })
  //   .not('deal_id', 'is', null)
  //   .select(`
  //     *,
  //     deal:deal_id (*),
  //     milestone:milestone_id (*)
  //   `);

  let response = await service.selectByKey({
    selectKey: 'findOpenTasks',
    params: { assigneeId },
  });

  response = service.processResponse(response);

  return response?.data;
};

const findAllOpenTasks = async ({ companyId }) => {
  // let response = await service
  //   .select()
  //   .match({
  //     company_id: companyId,
  //     status: TASK_STATUSES.OPEN,
  //   })
  //   .not('deal_id', 'is', null)
  //   .select(`
  //     *,
  //     deal:deal_id (*),
  //     milestone:milestone_id (*)
  //   `);

  let response = await service.selectByKey({
    selectKey: 'findAllOpenTasks',
  });

  response = service.processResponse(response);
  
  return response?.data;
};
// --> tasks page methods


export default Object.assign(service, {
  createTask,
  updateTask,
  removeTask,
  getMilestoneOpenEmails,
  getMilestoneTasks,
  getDocumentRequestsByDealId,
  getClientDocumentRequestsByDealId,
  completePredefineTask,
  findRequiredOpenUnderContractTasks,
  getTasksByDealId,
  getTasksByTemplateId,
  getOpenTasksByDealIds,
  getMilestonesTasks,
  getNumOfUnsentEmails,
  // --> tasks page methods
  findOpenTasks,
  findAllOpenTasks,
  findTasksCompletedByRealtorInDeals,
  findTasksCompletedByClients,
  findClientTasksByDealId,
  // <-- tasks page methods
});
